/**
 * "Re-identification risk" in the asset drawer (Annexure H Part I 114).
 *
 * For a scanned PostgreSQL table: the band of the latest assessment with k,
 * the at-risk share and l, the rule the band follows, the columns by role
 * (the operator adds or removes quasi-identifiers and picks the sensitive
 * column), monitoring on or off, and every assessment so a change over time
 * is visible. Counts only: the controller never returns a value from the
 * table (reidentification/service.ts).
 */
import { useEffect, useState } from 'react';
import { Loader2, ScanSearch } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useCan } from '@/lib/access';
import { problemDetail } from '@/lib/api';
import {
  assessRisk,
  getRiskView,
  updateRiskSettings,
  type RiskAssessment,
  type RiskBand,
  type RiskColumn,
  type RiskSettingsInput,
  type RiskView,
} from '@/lib/reidentification-risk-api';

export const REIDENTIFICATION_RISK_PERMISSION = 'discovery:write';

const BAND_VARIANT: Record<RiskBand, 'critical' | 'warning' | 'success'> = { high: 'critical', medium: 'warning', low: 'success' };
const BAND_WORD: Record<RiskBand, string> = { high: 'High', medium: 'Medium', low: 'Low' };

function istTime(iso: string): string {
  return `${new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })} IST`;
}

export function percent(share: number): string {
  return `${(share * 100).toFixed(1)}%`;
}

/** "all 43 rows" or "a sample of 1,012 of about 5,000 rows". */
export function coverageLabel(a: Pick<RiskAssessment, 'sampled' | 'rowsAssessed' | 'tableRows'>): string {
  const n = (x: number) => x.toLocaleString('en-IN');
  return a.sampled ? `a sample of ${n(a.rowsAssessed)} of about ${n(a.tableRows)} rows` : `all ${n(a.rowsAssessed)} rows`;
}

export function RiskSummary({ latest, rule }: { latest: RiskAssessment | undefined; rule: string }) {
  return (
    <div className="space-y-1.5">
      {latest ? (
        <div className="glass-lite rounded-md p-2 text-xs space-y-1" data-testid="reid-latest">
          <div className="flex items-center gap-2">
            <Badge variant={BAND_VARIANT[latest.band]} className="text-[10px]">{BAND_WORD[latest.band]}</Badge>
            <span>k = {latest.k ?? '-'}</span>
            <span>at risk {percent(latest.atRiskShare)} ({latest.atRiskRecords.toLocaleString('en-IN')})</span>
            {latest.sensitiveColumn && <span>l = {latest.l ?? '-'} on {latest.sensitiveColumn}</span>}
          </div>
          <div className="text-muted-foreground">
            Over {coverageLabel(latest)}, {istTime(latest.assessedAt)}.
          </div>
          {latest.sampled && (
            <div className="text-warning" data-testid="reid-sampled">
              Measured on a sample: k and the at-risk share describe the sample, not the whole table.
            </div>
          )}
          {latest.directIdentifiers.some((d) => d.nonNullRecords > 0) && (
            <div className="text-sev-high">
              Direct identifiers in clear: {latest.directIdentifiers.filter((d) => d.nonNullRecords > 0).map((d) => d.column).join(', ')}
            </div>
          )}
        </div>
      ) : (
        <div className="text-xs text-muted-foreground">Not assessed yet.</div>
      )}
      <div className="text-[10px] text-muted-foreground" data-testid="reid-rule">Rule: {rule}</div>
    </div>
  );
}

export function RiskColumnsForm({
  columns,
  sensitiveColumn,
  canEdit,
  onToggleQuasi,
  onSensitive,
}: {
  columns: RiskColumn[];
  sensitiveColumn: string | null;
  canEdit: boolean;
  onToggleQuasi: (column: string, quasi: boolean) => void;
  onSensitive: (column: string | null) => void;
}) {
  const candidates = columns.filter((c) => c.role === 'other');
  return (
    <div className="space-y-1.5">
      <table className="w-full text-xs">
        <tbody>
          {columns.map((c) => (
            <tr key={c.column} className="border-t border-border/30">
              <td className="py-1 font-mono">{c.column}</td>
              <td className="py-1">{c.label && <Badge variant="glass" className="text-[10px]">{c.label}</Badge>}</td>
              <td className="py-1 text-right">
                {c.role === 'direct' ? (
                  <span className="text-sev-high">Direct identifier</span>
                ) : (
                  <label className="inline-flex items-center gap-1">
                    <input
                      type="checkbox"
                      aria-label={`Quasi-identifier: ${c.column}`}
                      checked={c.role === 'quasi'}
                      disabled={!canEdit}
                      onChange={(e) => onToggleQuasi(c.column, e.target.checked)}
                    />
                    Quasi-identifier
                  </label>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <label className="flex items-center gap-2 text-xs">
        <span className="text-muted-foreground">Sensitive column (l-diversity)</span>
        <select
          aria-label="Sensitive column"
          value={sensitiveColumn ?? ''}
          disabled={!canEdit}
          onChange={(e) => onSensitive(e.target.value || null)}
          className="text-xs bg-background/50 border border-border/50 rounded-md px-1.5 py-1"
        >
          <option value="">None</option>
          {candidates.map((c) => (
            <option key={c.column} value={c.column}>{c.column}</option>
          ))}
        </select>
      </label>
    </div>
  );
}

export function RiskHistory({ history }: { history: RiskAssessment[] }) {
  if (history.length === 0) return null;
  return (
    <table className="w-full text-[10px]" data-testid="reid-history">
      <thead>
        <tr className="text-muted-foreground text-left">
          <th className="font-normal">When</th>
          <th className="font-normal">Band</th>
          <th className="font-normal">k</th>
          <th className="font-normal">At risk</th>
          <th className="font-normal">l</th>
          <th className="font-normal">Rows</th>
          <th className="font-normal">By</th>
        </tr>
      </thead>
      <tbody>
        {history.map((a) => (
          <tr key={a.id} className="border-t border-border/30">
            <td className="py-0.5">{istTime(a.assessedAt)}</td>
            <td><Badge variant={BAND_VARIANT[a.band]} className="text-[9px]">{BAND_WORD[a.band]}</Badge></td>
            <td>{a.k ?? '-'}</td>
            <td>{percent(a.atRiskShare)}</td>
            <td>{a.l ?? '-'}</td>
            <td>{a.sampled ? `sample of ${a.rowsAssessed.toLocaleString('en-IN')}` : a.rowsAssessed.toLocaleString('en-IN')}</td>
            <td className="truncate max-w-[8rem]">{a.trigger === 'scheduled' ? 'scheduled check' : a.assessedBy}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function ReidentificationRiskPanel({ assetId }: { assetId: string }) {
  const canEdit = useCan(REIDENTIFICATION_RISK_PERMISSION);
  const [view, setView] = useState<RiskView | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setView(null);
    setError(null);
    getRiskView(assetId)
      .then((v) => live && setView(v))
      .catch((e: unknown) => live && setError(problemDetail(e, 'Could not load the re-identification risk')));
    return () => {
      live = false;
    };
  }, [assetId]);

  async function act(fn: () => Promise<unknown>, failure: string) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setView(await getRiskView(assetId));
    } catch (e) {
      setError(problemDetail(e, failure));
    } finally {
      setBusy(false);
    }
  }

  function save(input: RiskSettingsInput) {
    void act(() => updateRiskSettings(assetId, input), 'The settings could not be saved');
  }

  function toggleQuasi(column: string, quasi: boolean) {
    if (!view) return;
    const s = view.settings;
    const without = (xs: string[]) => xs.filter((x) => x !== column);
    const classifiedQuasi = view.columns.find((c) => c.column === column)?.classifiedRole === 'quasi';
    save({
      quasiAdded: quasi && !classifiedQuasi ? [...without(s.quasiAdded), column] : without(s.quasiAdded),
      quasiRemoved: !quasi && classifiedQuasi ? [...without(s.quasiRemoved), column] : without(s.quasiRemoved),
    });
  }

  return (
    <div data-testid="reidentification-risk-panel">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Re-identification risk</div>
      {!view && !error && (
        <div className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Loading…</div>
      )}
      {view && (
        <div className="space-y-2">
          <RiskSummary latest={view.history[0]} rule={view.rule} />
          {view.unavailableReason ? (
            <div className="text-xs text-muted-foreground" data-testid="reid-unavailable">{view.unavailableReason}</div>
          ) : (
            <>
              <RiskColumnsForm
                columns={view.columns}
                sensitiveColumn={view.settings.sensitiveColumn}
                canEdit={canEdit && !busy}
                onToggleQuasi={toggleQuasi}
                onSensitive={(c) => save({ sensitiveColumn: c })}
              />
              <div className="flex items-center gap-3 text-xs">
                <label className="inline-flex items-center gap-1">
                  <input
                    type="checkbox"
                    aria-label="Monitor"
                    checked={view.settings.monitor}
                    disabled={!canEdit || busy}
                    onChange={(e) => save({ monitor: e.target.checked })}
                  />
                  Monitor (re-assessed on the rediscovery schedule; a new row when the band changes)
                </label>
              </div>
              {view.settings.monitor && view.settings.lastMonitoredAt && (
                <div className="text-[10px] text-muted-foreground">
                  Last scheduled check {istTime(view.settings.lastMonitoredAt)}: {view.settings.lastMonitoredOutcome}
                </div>
              )}
              <Button
                size="sm"
                variant="glass"
                disabled={!canEdit || busy}
                onClick={() => void act(() => assessRisk(assetId), 'The assessment could not be made')}
                data-testid="reid-assess"
              >
                <ScanSearch className="h-3.5 w-3.5 mr-1" /> {busy ? 'Working…' : 'Assess now'}
              </Button>
              {!canEdit && (
                <div className="text-[10px] text-muted-foreground">Only a role that may run scans can assess or change these settings.</div>
              )}
            </>
          )}
          <RiskHistory history={view.history} />
        </div>
      )}
      {error && <div className="text-[10px] text-sev-high mt-1">{error}</div>}
    </div>
  );
}
